import { BodyDiff, ConfigDiff, TagsDiff } from "@/components/recipes/diff-panel";
import { VersionPicker } from "@/components/recipes/version-picker";
import { ErrorState } from "@/components/ui/error-state";
import { PageHeaderV2 } from "@/components/ui/page-header-v2";
import { TableSkeleton } from "@/components/ui/skeleton-v2";
import { useNavigate, useParams } from "@tanstack/react-router";
import { ArrowLeft } from "lucide-react";
import { useEffect, useState } from "react";
import { useRecipe, useRecipeVersions } from "./queries";

export function RecipeVersionsPage() {
  const { recipeId } = useParams({ from: "/recipes/$recipeId/versions" });
  const navigate = useNavigate();
  const { data: recipe, isLoading: recipeLoading, error: recipeError } = useRecipe(recipeId);
  const {
    data: versionsData,
    isLoading: versionsLoading,
    error: versionsError,
  } = useRecipeVersions(recipeId);

  const [fromVersion, setFromVersion] = useState<number | null>(null);
  const [toVersion, setToVersion] = useState<number | null>(null);

  const versions = versionsData?.items || [];

  // Auto-select latest two versions when data loads
  useEffect(() => {
    if (versions.length >= 2 && fromVersion === null && toVersion === null) {
      const sorted = [...versions].sort((a, b) => b.version_no - a.version_no);
      setFromVersion(sorted[1]?.version_no ?? null);
      setToVersion(sorted[0]?.version_no ?? null);
    } else if (versions.length === 1 && fromVersion === null && toVersion === null) {
      setFromVersion(versions[0]?.version_no ?? null);
      setToVersion(versions[0]?.version_no ?? null);
    }
  }, [versions, fromVersion, toVersion]);

  // Fetch the two selected versions
  const { data: fromRecipe } = useRecipe(recipeId, fromVersion || undefined);
  const { data: toRecipe } = useRecipe(recipeId, toVersion || undefined);

  const error = recipeError || versionsError;
  const isLoading = recipeLoading || versionsLoading;

  if (error) {
    return (
      <div className="flex flex-col gap-6">
        <PageHeaderV2 eyebrow="Knowledge" title="Recipe Versions" />
        <ErrorState message={(error as Error).message} onRetry={() => window.location.reload()} />
      </div>
    );
  }

  if (isLoading || !recipe) {
    return (
      <div className="flex flex-col gap-6">
        <PageHeaderV2 eyebrow="Knowledge" title="Loading…" />
        <TableSkeleton rows={8} />
      </div>
    );
  }

  const handleBack = () => {
    navigate({ to: "/recipes/$recipeId", params: { recipeId: recipe.id } });
  };

  const showDiff = fromRecipe && toRecipe && fromVersion !== null && toVersion !== null;

  return (
    <div className="flex flex-col gap-6">
      <PageHeaderV2
        eyebrow="Knowledge"
        title={`${recipe.name} · Versions`}
        meta={
          <span className="text-muted">
            {versions.length} version{versions.length === 1 ? "" : "s"}
          </span>
        }
        action={
          <button
            onClick={handleBack}
            className="inline-flex items-center gap-2 rounded-md border border-line bg-surface px-3 py-1.5 text-sm font-medium text-muted transition-colors hover:bg-code-bg"
            type="button"
          >
            <ArrowLeft className="size-4" />
            Back
          </button>
        }
      />

      {versions.length === 0 ? (
        <div className="rounded-lg border border-line bg-surface p-8 text-center">
          <p className="text-muted">No versions committed yet.</p>
        </div>
      ) : (
        <>
          {/* Version picker */}
          <div className="rounded-lg border border-line bg-surface p-4">
            <h3 className="mb-3 font-mono text-xs uppercase tracking-wider text-muted">
              Select Versions to Compare
            </h3>
            <VersionPicker
              // biome-ignore lint/suspicious/noExplicitAny: type alignment between openapi and component
              versions={versions as any}
              from={fromVersion}
              to={toVersion}
              onFromChange={setFromVersion}
              onToChange={setToVersion}
            />
          </div>

          {/* Diff panels */}
          {showDiff && (
            <div className="space-y-6">
              {/* Body diff */}
              <div className="rounded-lg border border-line bg-surface p-4">
                <h3 className="mb-3 font-mono text-xs uppercase tracking-wider text-muted">
                  Body Changes
                </h3>
                <BodyDiff oldBody={fromRecipe.body} newBody={toRecipe.body} />
              </div>

              {/* Config diff */}
              {(fromRecipe.training_config || toRecipe.training_config) && (
                <div className="rounded-lg border border-line bg-surface p-4">
                  <h3 className="mb-3 font-mono text-xs uppercase tracking-wider text-muted">
                    Training Config Changes
                  </h3>
                  <ConfigDiff
                    oldConfig={fromRecipe.training_config || null}
                    newConfig={toRecipe.training_config || null}
                  />
                </div>
              )}

              {/* Tags diff */}
              <div className="rounded-lg border border-line bg-surface p-4">
                <h3 className="mb-3 font-mono text-xs uppercase tracking-wider text-muted">
                  Tag Changes
                </h3>
                <TagsDiff oldTags={fromRecipe.tags} newTags={toRecipe.tags} />
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
